import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
  mockNewAffiliateClicks,
} from '@features/admin/reports/reportData';
import { TReportNewAffiliateClicksData, ReportPeriod } from '@src/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ReportGroupBy } from '@src/types/reports';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { toast } from 'react-toastify';
import {
  getChartColor,
  useReportChartData,
} from '@src/hooks/useReportChartData';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportNewAffiliateClicksData;
    color?: string;
    name?: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[135px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] mb-1">
          {payload[0]?.payload?.period}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="text-center">
            <span className="text-white text-xs">{entry.name}: </span>
            <span
              className="text-xl font-semibold"
              style={{ color: entry.color }}
            >
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const NewAffiliateClicks: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<TReportNewAffiliateClicksData[]>([]);

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.TODAY,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportNewAffiliateClicksData>({
      data,
      selectedItems: ['unique_clicks', 'all_clicks'],
      getAccessField: (item) => item.period,
      getMainDataField: (item, key) => {
        if (key === 'all_clicks') return item.all_clicks || 0;
        if (key === 'unique_clicks') return item.unique_clicks || 0;

        return 0;
      },
      getPreviousPeriodField: (item, key) => {
        if (key === 'all_clicks') return item.all_clicks_previous || 0;
        if (key === 'unique_clicks') return item.unique_clicks_previous || 0;

        return 0;
      },
      getPreviousYearField: (item, key) => {
        if (key === 'all_clicks') return item.all_clicks_previous_year || 0;
        if (key === 'unique_clicks')
          return item.unique_clicks_previous_year || 0;

        return 0;
      },
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      columnTitleFormatter: (key) => {
        if (key === 'all_clicks') return 'ALL CLICKS';
        if (key === 'unique_clicks') return 'UNIQUE CLICKS';

        return key.toUpperCase();
      },
      includeTotalsRow: true,
    });

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.period,
    getMainDataField: (item, key) => {
      if (key === 'all_clicks') return item.all_clicks || 0;
      if (key === 'unique_clicks') return item.unique_clicks || 0;

      return 0;
    },
    getPreviousPeriodField: (item, key) => {
      if (key === 'all_clicks') return item.all_clicks_previous || 0;
      if (key === 'unique_clicks') return item.unique_clicks_previous || 0;

      return 0;
    },
    getPreviousYearField: (item, key) => {
      if (key === 'all_clicks') return item.all_clicks_previous_year || 0;
      if (key === 'unique_clicks') return item.unique_clicks_previous_year || 0;

      return 0;
    },
  });

  const handleRunReport = () => {
    filters.setIsLoading(true);
    try {
      setData(mockNewAffiliateClicks);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = () => {
    toast.info('Export functionality will be available when API is ready');
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        New Affiliate Clicks
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Line
                activeDot={{ r: 6 }}
                dataKey="all_clicks"
                dot={{ fill: getChartColor(0), r: 4 }}
                name="All Clicks"
                stroke={getChartColor(0)}
                strokeWidth={2}
                type="monotone"
              />
              <Line
                activeDot={{ r: 6 }}
                dataKey="unique_clicks"
                dot={{ fill: getChartColor(1), r: 4 }}
                name="Unique Clicks"
                stroke={getChartColor(1)}
                strokeWidth={2}
                type="monotone"
              />
              {filters.compareToPreviousPeriod && (
                <>
                  <Line
                    activeDot={{ r: 6 }}
                    dataKey="unique_clicks_previous_period"
                    dot={{ fill: getChartColor(3), r: 4 }}
                    name="Unique Clicks (Previous Period)"
                    stroke={getChartColor(3)}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    type="monotone"
                  />
                  <Line
                    activeDot={{ r: 6 }}
                    dataKey="all_clicks_previous_period"
                    dot={{ fill: getChartColor(2), r: 4 }}
                    name="All Clicks (Previous Period)"
                    stroke={getChartColor(2)}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    type="monotone"
                  />
                </>
              )}
              {filters.compareToSamePeriodPreviousYear && (
                <>
                  <Line
                    activeDot={{ r: 6 }}
                    dataKey="unique_clicks_previous_year"
                    dot={{ fill: getChartColor(5), r: 4 }}
                    name="Unique Clicks (Previous Year)"
                    stroke={getChartColor(5)}
                    strokeDasharray="3 3"
                    strokeWidth={2}
                    type="monotone"
                  />
                  <Line
                    activeDot={{ r: 6 }}
                    dataKey="all_clicks_previous_year"
                    dot={{ fill: getChartColor(4), r: 4 }}
                    name="All Clicks (Previous Year)"
                    stroke={getChartColor(4)}
                    strokeDasharray="3 3"
                    strokeWidth={2}
                    type="monotone"
                  />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
